import * as React from 'react';
import { CheckMark, Close, Conflict, IndicationsAlert } from '@components/atoms/icons';
import { LoadingSpinner } from '@components/atoms/loading-spinner/loading-spinner';
import { LoadingSpinnerSize } from '@components/atoms/loading-spinner/loading-spinner.types';
import { NotificationBadge } from '@components/atoms/notification-badge';
import { cn } from '@utils';
import { PositionToggleButton } from './position-toggle-button';
import { TONE_ICON_CLASS, TONE_ICON_CONTAINER_CLASS, toBadgeVariant } from './tone';
import { FloatingPanelToneEnum } from './types';
import type { IPositionMoveControl } from './types';

interface ICollapsedBadgeProps {
	count: number;
	tone: FloatingPanelToneEnum;
	icon?: React.ReactNode;
	onClick?: () => void;
	onDismiss?: () => void;
	moveControl?: IPositionMoveControl;
	shouldPulse?: boolean;
	expandAriaLabel: (count: number, tone: FloatingPanelToneEnum) => string;
	dismissAriaLabel: string;
}

interface IDefaultToneIconProps {
	tone: FloatingPanelToneEnum;
}

const DefaultToneIcon: React.FC<IDefaultToneIconProps> = ({ tone }) => {
	switch (tone) {
		case FloatingPanelToneEnum.SUCCESS:
			return <CheckMark className={cn('size-4', TONE_ICON_CLASS[tone])} />;
		case FloatingPanelToneEnum.WARNING:
			return <IndicationsAlert className={cn('size-4', TONE_ICON_CLASS[tone])} />;
		case FloatingPanelToneEnum.ERROR:
			return <Conflict className={cn('size-4', TONE_ICON_CLASS[tone])} />;
		case FloatingPanelToneEnum.LOADING:
			return <LoadingSpinner size={LoadingSpinnerSize.SM} className="size-4" />;
		default: {
			const _exhaustive: never = tone;
			return _exhaustive;
		}
	}
};

export const CollapsedBadge: React.FC<ICollapsedBadgeProps> = ({
	count,
	tone,
	icon,
	onClick,
	onDismiss,
	moveControl,
	shouldPulse = false,
	expandAriaLabel,
	dismissAriaLabel,
}) => {
	return (
		<div
			className={cn(
				'flex items-center',
				'bg-white',
				'border border-borderPrimary',
				'rounded-xl',
				'shadow-[0_4px_20px_-4px_rgba(0,0,0,0.15),0_0_0_1px_rgba(0,0,0,0.05)]',
				'transition-all duration-200',
				'hover:shadow-[0_8px_30px_-4px_rgba(0,0,0,0.2),0_0_0_1px_rgba(0,0,0,0.05)]',
				'hover:-translate-y-0.5',
				shouldPulse && 'animate-pulse'
			)}
			data-testid="collapsed-badge"
			data-tone={tone}
			data-pulse={shouldPulse || undefined}
		>
			<button
				type="button"
				onClick={onClick}
				className={cn(
					'flex items-center gap-2 px-3 py-2.5',
					'rounded-xl',
					'cursor-pointer transition-colors',
					'active:shadow-[0_2px_10px_-4px_rgba(0,0,0,0.15)]'
				)}
				aria-label={expandAriaLabel(count, tone)}
				data-testid="collapsed-badge-expand"
			>
				<div
					className={cn(
						'flex items-center justify-center size-6 rounded-full',
						TONE_ICON_CONTAINER_CLASS[tone]
					)}
					data-testid="collapsed-badge-icon"
				>
					{icon || <DefaultToneIcon tone={tone} />}
				</div>
				<NotificationBadge count={count} variant={toBadgeVariant(tone)} size="md" />
			</button>
			{moveControl && (
				<PositionToggleButton
					moveControl={moveControl}
					testId="collapsed-badge-move"
					compact
				/>
			)}
			{onDismiss && (
				<button
					type="button"
					onClick={onDismiss}
					className="mr-2 size-5 flex items-center justify-center text-tertiary hover:text-primary hover:bg-black/5 rounded-full transition-colors"
					aria-label={dismissAriaLabel}
					title={dismissAriaLabel}
					data-testid="collapsed-badge-dismiss"
				>
					<Close className="size-3" />
				</button>
			)}
		</div>
	);
};
